import { Alert, Button, Col, Flex, Form, Input, message, Row, Space, Spin } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useEffect, useRef, useState } from "react";
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  FlagOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  ShopOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Typography } from "antd";
import dayjs from "dayjs";
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

const { Title, Text, Link } = Typography;

interface Props {
  refresh?: () => void;
  userData?: any;
  closeModal?: () => void;

  isEditing: boolean;
  onBeginEdit: () => void;
  onEndEdit: () => void;
}

type AdditionalItem = { key?: string; value?: string };

export const AditionalData = ({ refresh, userData, closeModal, isEditing, onBeginEdit, onEndEdit }: Props) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [editingAditionalData, setEditingAditionalData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  console.log('userData', userData)

  const toItems = (additionalData?: Record<string, any>): AdditionalItem[] => {
    if (!additionalData || typeof additionalData !== "object") return [];
    return Object.entries(additionalData).map(([k, v]) => ({
      key: String(k ?? ""),
      value: v == null ? "" : String(v),
    }));
  };

  const normalizeKey = (input: string) =>
    input
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // quita acentos
      .replace(/[^a-z0-9]+/g, "-") // espacios y símbolos -> "-"
      .replace(/^-+|-+$/g, ""); // recorta "-"

  const toAdditionalDataObject = (
    items: AdditionalItem[],
  ): Record<string, string> => {
    const out: Record<string, string> = {};

    for (const row of items ?? []) {
      const rawKey = String(row?.key ?? "").trim();
      const rawValue = String(row?.value ?? "").trim();

      if (!rawKey || !rawValue) continue;

      const k = normalizeKey(rawKey);
      if (!k) continue;

      out[k] = rawValue;
    }

    return out;
  };

  useEffect(() => {
    if (!isEditing) return;

    const items = toItems(userData?.additionalData);


    form.setFieldsValue({
      items: items.length ? items : [{ key: "", value: "" }],
    });
  }, [isEditing, userData?.additionalData, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const { items } = form.getFieldsValue(["items"]) as { items?: AdditionalItem[] };

      const additionalData = toAdditionalDataObject(items ?? []);

      const payload = {
        description: userData?.description ?? "",
        isActive: userData?.isActive ?? true,
        additionalData,
      };
      await axiosInstance.patch(
        `${API_BASE_URL}/contacts/${userData?.contactId}`,
        payload,
      );

      message.success("Additional data updated");
      onEndEdit();
      refresh?.();
      closeModal?.();
    } catch (err) {
      console.error(err);
      message.error("Error updating additional data");
    } finally {
      setLoading(false);
    }
  };

  const hasItems = toItems(userData?.additionalData).length > 0;


  if (isEditing) {
    return (
      <Spin spinning={loading} style={{ height: "100%" }}>
        <Form
          form={form}
          layout="vertical"
          // LateralModal no le pone margen al body: lo pone el panel (xcons-docs#509).
          style={{ height: "86vh", padding: "16px 24px", boxSizing: "border-box" }}
        >
          <Form.Item label="Puntoseco:" style={{ marginBottom: 8 }}>
            <Form.List name="items">
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: "100%" }} size={12}>
                  {/* Filas existentes */}
                  {fields.map((field) => (
                    <Row key={field.key} gutter={8} align="middle" wrap={false}>
                      <Col flex="auto">
                        <Space.Compact style={{ width: "100%" }}>
                          <Form.Item
                            name={[field.name, "key"]}
                            noStyle
                            rules={[{ required: true, message: "Requerido" }]}
                          >
                            <Input placeholder="Property" />
                          </Form.Item>

                          {/* El ":" como separador visual */}
                          <Input
                            value=":"
                            disabled
                            style={{
                              width: 44,
                              textAlign: "center",
                              cursor: "default",
                              backgroundColor: "transparent",
                              color: "rgba(0,0,0,0.45)",
                            }}
                          />

                          <Form.Item
                            name={[field.name, "value"]}
                            noStyle
                            rules={[{ required: true, message: "Requerido" }]}
                          >
                            <Input placeholder="Option" />
                          </Form.Item>
                        </Space.Compact>
                      </Col>

                      <Col flex="40px" style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </Col>
                    </Row>
                  ))}

                  {/* Fila “Add” (inputs disabled + botón +) */}
                  <Row gutter={8} align="middle" wrap={false}>
                    <Col flex="auto">
                      <Space.Compact style={{ width: "100%" }}>
                        <Input placeholder="Property" disabled />
                        <Input
                          value=":"
                          disabled
                          style={{
                            width: 44,
                            textAlign: "center",
                            cursor: "default",
                            color: "rgba(0,0,0,0.45)",
                          }}
                        />
                        <Input placeholder="Option" disabled />
                      </Space.Compact>
                    </Col>

                    <Col flex="40px" style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => add({ key: "", value: "" })}
                      />
                    </Col>
                  </Row>
                </Space>
              )}
            </Form.List>
          </Form.Item>

          <Flex
            justify="end"
            gap={8}
            style={{ position: "absolute", bottom: 16, right: 24 }}
          >
            <Button danger onClick={onEndEdit}>
              {t("buttons.cancel")}
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              icon={<CheckOutlined />} iconPosition="end"
              disabled={hasErrors}
            >
              {t("buttons.save")}
            </Button>
          </Flex>
        </Form>
      </Spin>
    );
  }

  const additionalEntries = Object.entries(userData?.additionalData ?? {});

  const formatLabel = (key: string) =>
    key
      .replace(/-/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <Flex vertical gap={8}>
      <Text strong>Aditional data</Text>

      <Flex vertical gap={8} style={{ marginTop: 18 }}>
        <Text strong style={{ textTransform: 'capitalize' }}>
          <ShopOutlined /> {userData?.storeCode}:
        </Text>

        {additionalEntries.length > 0 ? (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {additionalEntries.map(([key, value]) => (
              <li key={key}>
                <Text>
                  <strong>{formatLabel(key)}:</strong> {String(value)}
                </Text>
              </li>
            ))}
          </ul>
        ) : (
          <Text type="secondary">No additional data</Text>
        )}
      </Flex>

      <Flex>
        <Button
          size="small"
          shape="round"
          icon={<EditOutlined />}
          iconPosition="end"
          style={{ marginTop: 22, width: 68 }}
          onClick={onBeginEdit}
        >
          Edit
        </Button>
      </Flex>
    </Flex>
  );
};
